<script setup>
import { computed, onActivated, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useLocalizedTime } from 'shared/composables/useLocalizedTime';
import { useFormsStore } from 'dashboard/stores/forms';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BarChart from 'shared/components/charts/BarChart.vue';

const props = defineProps({
  formId: { type: [String, Number], required: true },
});

const STATUS_PILL = {
  ok: 'bg-n-teal-3 text-n-teal-11',
  partial: 'bg-n-amber-3 text-n-amber-11',
  duplicate: 'bg-n-blue-3 text-n-blue-11',
  rejected: 'bg-n-alpha-2 text-n-slate-11',
};

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { dateTime } = useLocalizedTime();
const formsStore = useFormsStore();

const submissions = ref([]);
const meta = ref({ totalCount: 0, page: 1 });
const isLoading = ref(true);
const formName = ref('');
const PER_PAGE = 25;

// Conversão por domínio de origem (US 05, CA 5.5): um domínio sem tráfego ainda aparece aqui,
// zerado (FA 5.3) — o backend já enumera a partir da whitelist do formulário.
const domains = ref([]);
const isLoadingAnalytics = ref(true);

const hasNext = computed(
  () => meta.value.page * PER_PAGE < meta.value.totalCount
);

const load = async (page = 1) => {
  isLoading.value = true;
  try {
    const result = await formsStore.getSubmissions(Number(props.formId), {
      page,
    });
    submissions.value = result.submissions;
    meta.value = result.meta;
  } catch {
    useAlert(t('FORM_BUILDER.EDITOR.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const loadAnalytics = async () => {
  isLoadingAnalytics.value = true;
  try {
    domains.value = await formsStore.getAnalytics(Number(props.formId));
  } catch {
    domains.value = [];
  } finally {
    isLoadingAnalytics.value = false;
  }
};

const analyticsChart = computed(() => ({
  labels: domains.value.map(row => row.domain),
  datasets: [
    {
      label: t('FORM_BUILDER.SUBMISSIONS.ANALYTICS.CONVERSION_RATE'),
      backgroundColor: 'rgb(31, 147, 255)',
      data: domains.value.map(row => row.conversionRate),
    },
  ],
}));

const analyticsChartOptions = {
  indexAxis: 'y',
  plugins: { legend: { display: false } },
  scales: {
    x: { beginAtZero: true, max: 100 },
    y: { grid: { drawOnChartArea: false } },
  },
};

const analyticsChartHeight = computed(
  () => `${domains.value.length * 36 + 24}px`
);

// Origem principal do envio: campanha ou origem, o que houver (UTMs gravados no momento do envio).
const originOf = submission => {
  const { utm_source: source, utm_campaign: campaign } =
    submission.attribution || {};
  return [source, campaign].filter(Boolean).join(' · ');
};

const goBack = () => router.push(accountScopedRoute('forms_settings_index'));

const goToContact = id =>
  router.push(accountScopedRoute('contacts_edit', { contactId: id }));

onMounted(async () => {
  const form = formsStore.getFormById(Number(props.formId));
  formName.value = form
    ? form.name
    : (await formsStore.show(Number(props.formId))).name;
  load();
  loadAnalytics();
});

watch(
  () => props.formId,
  () => {
    load();
    loadAnalytics();
  }
);

// SettingsWrapper cacheia por route.fullPath (<keep-alive>): reabrir os Envios do MESMO form
// reaproveita a instância sem passar de novo por onMounted, deixando a lista e o analytics
// desatualizados (ex. uma submissão nova feita enquanto você estava em outra tela).
onActivated(() => {
  load();
  loadAnalytics();
});
</script>

<template>
  <SettingsLayout>
    <BaseSettingsHeader
      :title="t('FORM_BUILDER.SUBMISSIONS.TITLE')"
      :description="formName"
    >
      <template #actions>
        <Button
          slate
          faded
          icon="i-lucide-arrow-left"
          :label="t('FORM_BUILDER.BACK')"
          @click="goBack"
        />
      </template>
    </BaseSettingsHeader>

    <section
      v-if="!isLoadingAnalytics && domains.length"
      class="mt-6 border rounded-lg border-n-weak"
    >
      <h2 class="p-3 font-medium text-n-slate-12">
        {{ t('FORM_BUILDER.SUBMISSIONS.ANALYTICS.TITLE') }}
      </h2>
      <div class="flex flex-col gap-4 px-3 pb-3 md:flex-row md:items-start">
        <div class="flex-1 overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-n-slate-10">
                <th class="py-1 pr-3">
                  {{ t('FORM_BUILDER.SUBMISSIONS.ANALYTICS.DOMAIN') }}
                </th>
                <th class="py-1 pr-3">
                  {{ t('FORM_BUILDER.SUBMISSIONS.ANALYTICS.SUBMISSIONS') }}
                </th>
                <th class="py-1 pr-3">
                  {{ t('FORM_BUILDER.SUBMISSIONS.ANALYTICS.LEADS') }}
                </th>
                <th class="py-1 pr-3">
                  {{ t('FORM_BUILDER.SUBMISSIONS.ANALYTICS.DEALS') }}
                </th>
                <th class="py-1">
                  {{ t('FORM_BUILDER.SUBMISSIONS.ANALYTICS.CONVERSION_RATE') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in domains"
                :key="row.domain"
                class="border-t border-n-weak text-n-slate-12"
              >
                <td class="py-1 pr-3">{{ row.domain }}</td>
                <td class="py-1 pr-3">{{ row.submissionsCount }}</td>
                <td class="py-1 pr-3">{{ row.leadsCount }}</td>
                <td class="py-1 pr-3">{{ row.dealsCount }}</td>
                <td class="py-1">{{ row.conversionRate }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          class="hidden shrink-0 md:block md:w-64"
          :style="{ height: analyticsChartHeight }"
        >
          <BarChart
            :collection="analyticsChart"
            :chart-options="analyticsChartOptions"
          />
        </div>
      </div>
    </section>

    <div class="mt-6 overflow-x-auto">
      <p v-if="isLoading" class="text-sm text-n-slate-10">
        {{ t('FORM_BUILDER.SUBMISSIONS.LOADING') }}
      </p>
      <p v-else-if="!submissions.length" class="text-sm text-n-slate-10">
        {{ t('FORM_BUILDER.SUBMISSIONS.EMPTY') }}
      </p>
      <table v-else class="w-full text-sm">
        <thead>
          <tr class="text-left text-n-slate-10">
            <th class="py-2 pr-4">
              {{ t('FORM_BUILDER.SUBMISSIONS.COLUMNS.DATE') }}
            </th>
            <th class="py-2 pr-4">
              {{ t('FORM_BUILDER.SUBMISSIONS.COLUMNS.CONTACT') }}
            </th>
            <th class="py-2 pr-4">
              {{ t('FORM_BUILDER.SUBMISSIONS.COLUMNS.DEAL') }}
            </th>
            <th class="py-2 pr-4">
              {{ t('FORM_BUILDER.SUBMISSIONS.COLUMNS.ORIGIN') }}
            </th>
            <th class="py-2">
              {{ t('FORM_BUILDER.SUBMISSIONS.COLUMNS.STATUS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="submission in submissions"
            :key="submission.id"
            class="border-t border-n-weak text-n-slate-12"
          >
            <td class="py-2 pr-4 whitespace-nowrap">
              {{ dateTime(submission.createdAt) }}
            </td>
            <td class="py-2 pr-4">
              <button
                v-if="submission.contact.id"
                type="button"
                class="text-left underline text-n-brand"
                @click="goToContact(submission.contact.id)"
              >
                {{
                  submission.contact.name ||
                  submission.contact.phoneNumber ||
                  submission.contact.email
                }}
              </button>
              <span v-else>—</span>
              <span
                v-if="submission.contact.phoneNumber"
                class="block text-xs text-n-slate-11"
              >
                {{ submission.contact.phoneNumber }}
              </span>
            </td>
            <td class="py-2 pr-4">{{ submission.deal.name || '—' }}</td>
            <td class="py-2 pr-4">
              {{
                originOf(submission) || t('FORM_BUILDER.SUBMISSIONS.NO_ORIGIN')
              }}
            </td>
            <td class="py-2">
              <span
                class="px-2 py-0.5 text-xs font-medium rounded-full"
                :class="STATUS_PILL[submission.status]"
                :title="
                  submission.status === 'ok'
                    ? ''
                    : t(
                        `FORM_BUILDER.SUBMISSIONS.STATUS_HELP.${submission.status.toUpperCase()}`
                      )
                "
              >
                {{
                  t(
                    `FORM_BUILDER.SUBMISSIONS.STATUSES.${submission.status.toUpperCase()}`
                  )
                }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="meta.totalCount > PER_PAGE"
        class="flex items-center gap-2 mt-4"
      >
        <Button
          sm
          slate
          faded
          :label="t('FORM_BUILDER.SUBMISSIONS.PREVIOUS')"
          :disabled="meta.page <= 1"
          @click="load(meta.page - 1)"
        />
        <span class="text-sm text-n-slate-11">
          {{ t('FORM_BUILDER.SUBMISSIONS.PAGE', { page: meta.page }) }}
        </span>
        <Button
          sm
          slate
          faded
          :label="t('FORM_BUILDER.SUBMISSIONS.NEXT')"
          :disabled="!hasNext"
          @click="load(meta.page + 1)"
        />
      </div>
    </div>
  </SettingsLayout>
</template>
